
import React, { useState, useEffect } from 'react';
import { Viewer, Worker } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import { zoomPlugin } from '@react-pdf-viewer/zoom';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import '@react-pdf-viewer/zoom/lib/styles/index.css';
import { toast } from 'react-toastify';
import { generatesummary } from '../../../../../services/CommonService';
import ReactMarkdown from 'react-markdown';
import { saveAs } from 'file-saver';
import { Document, Paragraph, TextRun, Packer } from 'docx';
import { LuSpeech } from 'react-icons/lu';
import { Button } from 'react-bootstrap';
import { jsPDF } from 'jspdf';
import { FaRegStopCircle, FaDownload, FaCopy, FaArrowLeft, FaSearchPlus, FaSearchMinus } from "react-icons/fa";
import { SiGooglegemini } from "react-icons/si";

const SummaryDetails = () => {
  const storedData = sessionStorage.getItem('summaryData');
  const docData = storedData ? JSON.parse(storedData) : null;
  const defaultLayoutPluginInstance = defaultLayoutPlugin();
  const zoomPluginInstance = zoomPlugin();
  const [activeTab, setActiveTab] = useState('short');
  const [summary, setSummary] = useState({ short: '', medium: '', large: '' });
  const [loading, setLoading] = useState({ short: false, medium: false, large: false });
  const [speechSpeed, setSpeechSpeed] = useState(1);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [viewMode, setViewMode] = useState('folder');
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [voices, setVoices] = useState([]);
  const [activeKeys, setActiveKeys] = useState([]);
  const [zoomLevel, setZoomLevel] = useState(1.0);
  const zoomLevels = [0.5, 0.75, 1.0, 1.25, 1.5, 2.0];

  const handleViewChange = (e) => {
    setViewMode(e.target.value);
  };

  const synth = window.speechSynthesis;
  let speechQueue = [];

  const handleClosePopup = () => {
    if (isSpeaking) {
      synth.cancel();
      speechQueue = [];
      setIsSpeaking(false);
    }
    setSummary({ short: '', medium: '', large: '' });
  };

  const handleSpeechToggle = () => {
    if (isSpeaking) {
      synth.cancel();
      speechQueue = [];
      setIsSpeaking(false);
    } else if (summary[activeTab]) {
      synth.cancel();
      speechQueue = [];
      const plainText = summary[activeTab].replace(/[*_#`]+/g, '');
      const chunks = plainText.match(/.{1,200}/g) || [plainText];
      let index = 0;

      const speakChunk = () => {
        if (index >= chunks.length) {
          setIsSpeaking(false);
          synth.cancel();
          speechQueue = [];
          return;
        }

        const utterance = new SpeechSynthesisUtterance(chunks[index]);
        utterance.rate = speechSpeed;
        utterance.volume = 1.0;
        if (selectedVoice) utterance.voice = selectedVoice;
        utterance.onend = () => {
          document.body.style.overflow = 'auto';
          index++;
          speakChunk();
        };
        utterance.onstart = () => {
          document.body.style.overflow = 'hidden';
        };
        setIsSpeaking(true);
        synth.speak(utterance);
      };

      if (synth.paused) synth.resume();
      speakChunk();
    }
  };

  const handleBack = () => {
    window.history.back();
  };


  useEffect(() => {
    const updateVoices = () => {
      const allVoices = synth.getVoices();
      setVoices(allVoices);
      const femaleVoice = allVoices.find(
        (voice) =>
          voice.name.toLowerCase().includes('female') ||
          voice.name.includes('Samantha') ||
          voice.name.includes('Victoria') ||
          voice.name.includes('Google US English')
      );
      if (femaleVoice) setSelectedVoice(femaleVoice);
    };

    synth.onvoiceschanged = updateVoices;
    updateVoices();
    return () => {
      synth.cancel();
      synth.onvoiceschanged = null;
    };
  }, []);

  const handleStop = () => {
    if (synth.speaking) {
      synth.cancel();
      speechQueue = [];
      setIsSpeaking(false);
    }
  };

  const handleAccordionClick = (key) => {
    if (isSpeaking) {
      synth.cancel();
      speechQueue = [];
      setIsSpeaking(false);
    }
    fetchSummary(key);
    setActiveKeys((prevKeys) =>
      prevKeys.includes(key)
        ? prevKeys.filter((k) => k !== key)
        : [...prevKeys, key]
    );
  };

  const fetchSummary = async (type) => {
    if (!docData?.envelope_id || !docData?.file_name) {
      toast.error('Missing document data. Please ensure envelope_id and file_name are provided.');
      return;
    }

    try {
      setLoading((prev) => ({ ...prev, [type]: true }));

      const payload = {
        envelope_id: Number(docData.envelope_id),
        file_name: docData.file_name,
        length: type,
      };

      const response = await generatesummary(payload);
      setSummary((prev) => ({
        ...prev,
        [type]: response.data?.summary || `No ${type} summary available`,
      }));
    } catch (error) {
      console.error(`Error fetching ${type} summary:`, {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
      });
      const errorMessage = error.response?.data?.message || `Failed to load ${type} summary`;
      toast.error(errorMessage);
    } finally {
      setLoading((prev) => ({ ...prev, [type]: false }));
    }
  };

  const handleZoomIn = () => {
    const currentIndex = zoomLevels.indexOf(zoomLevel);
    if (currentIndex < zoomLevels.length - 1) {
      const newZoomLevel = zoomLevels[currentIndex + 1];
      setZoomLevel(newZoomLevel);
      zoomPluginInstance.zoomTo(newZoomLevel);
    }
  };

  const handleZoomOut = () => {
    const currentIndex = zoomLevels.indexOf(zoomLevel);
    if (currentIndex > 0) {
      const newZoomLevel = zoomLevels[currentIndex - 1];
      setZoomLevel(newZoomLevel);
      zoomPluginInstance.zoomTo(newZoomLevel);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (!summary[tab]) {
      fetchSummary(tab);
    }
  };

  const copyToClipboard = (text) => {
    if (!text) {
      toast.error('No summary available to copy');
      return;
    }
    const plainText = text.replace(/[*_#`]+/g, '');
    navigator.clipboard
      .writeText(plainText)
      .then(() => toast.success('Summary copied to clipboard!'))
      .catch(() => toast.error('Failed to copy text'));
  };

  const downloadAsPDF = async (content, type) => {
    if (!content) {
      toast.error('No summary available to download');
      return;
    }
    try {
      const plainText = content.replace(/[*_#`]+/g, '');
      const doc = new jsPDF();
      doc.setFontSize(16);
      doc.text(`${type.charAt(0).toUpperCase() + type.slice(1)} Summary`, 10, 10);
      doc.setFontSize(12);
      const splitText = doc.splitTextToSize(plainText, 190);
      doc.text(splitText, 10, 20);
      doc.save(`${docData?.file_name || 'document'}_${type}_summary.pdf`);
      toast.success('Summary downloaded as PDF');
    } catch (error) {
      console.error('PDF download error:', error);
      toast.error('Failed to download PDF');
    }
  };

  const downloadAsDocx = async (content, type) => {
    if (!content) {
      toast.error('No summary available to download');
      return;
    }
    try {
      const plainText = content.replace(/[*_#`]+/g, '');
      const doc = new Document({
        sections: [
          {
            properties: {},
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: `${type.charAt(0).toUpperCase() + type.slice(1)} Summary`,
                    bold: true,
                    size: 24,
                  }),
                ],
                spacing: { after: 200 },
              }),
              ...plainText.split('\n').map(
                (line) =>
                  new Paragraph({
                    children: [new TextRun({ text: line, size: 20 })],
                    spacing: { after: 100 },
                  })
              ),
            ],
          },
        ],
      });

      const blob = await Packer.toBlob(doc);
      saveAs(blob, `${docData?.file_name || 'document'}_${type}_summary.docx`);
      toast.success('Summary downloaded as DOCX');
    } catch (error) {
      console.error('DOCX download error:', error);
      toast.error('Failed to download DOCX');
    }
  };

  return (
    <div className="container-fluid">
      <style jsx>{`
        .markdown-content {
          
          line-height: 1.6;
          color: #333;
        }

        .markdown-content h1 {
          font-size: 1.8rem;
          font-weight: 700;
          margin-bottom: 1rem;
          color: #2c3e50;
        }

         .gemini-footer {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 8px;
    background-color: #f8f9fa;
    border-top: 1px solid #ddd;
    font-size: 0.9rem;
    color: #6a737d;
    flex-shrink: 0;
  }

        .markdown-content h2 {
          font-size: 1.5rem;
          font-weight: 600;
          margin: 1.2rem 0 0.8rem;
          color: #2c3e50;
        }

        .markdown-content h3 {
          font-size: 1.3rem;
          font-weight: 500;
          margin: 1rem 0 0.6rem;
          color: #2c3e50;
        }

        .markdown-content h4 {
          font-size: 1.1rem;
          font-weight: 500;
          margin: 0.8rem 0 0.5rem;
          color: #2c3e50;
        }

        .markdown-content p {
          font-size: 1rem;
          margin: 0.5rem 0;
          color: #444;
        }

        .markdown-content ul,
        .markdown-content ol {
          margin: 0.5rem 0;
          padding-left: 2rem;
        }

        .markdown-content li {
          font-size: 1rem;
          margin-bottom: 0.4rem;
          color: #444;
        }

        .markdown-content table {
          width: 100%;
          border-collapse: collapse;
          margin: 1rem 0;
          background-color: #fff;
          border: 1px solid #ddd;
        }

        .markdown-content th,
        .markdown-content td {
          padding: 0.75rem;
          border: 1px solid #ddd;
          font-size: 1rem;
          text-align: left;
        }

        .markdown-content th {
          background-color: #f1f3f5;
          font-weight: 600;
          color: #2c3e50;
        }

        .markdown-content td {
          color: #444;
        }

        .markdown-content a {
          color: #007bff;
          text-decoration: none;
        }

        .markdown-content a:hover {
          text-decoration: underline;
        }

        .markdown-content blockquote {
          border-left: 4px solid #007bff;
          padding-left: 1rem;
          margin: 1rem 0;
          color: #555;
          font-style: italic;
        }

        .markdown-content code {
          background-color: #f8f9fa;
          padding: 0.2rem 0.4rem;
          border-radius: 3px;
          font-size: 0.95rem;
        }

        .markdown-content pre {
          background-color: #f8f9fa;
          padding: 1rem;
          border-radius: 5px;
          overflow-x: auto;
        }

        .action-buttons {
          display: flex;
          gap: 8px;
          margin-top: 15px;
          flex-wrap: wrap;
          justify-content: flex-end;
          align-items: center;
        }

        .action-btn {
          padding: 5px 10px;
          font-size: 0.8rem;
          border-radius: 4px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 5px;
          border: 1px solid #ddd;
          background-color: #f5f5f5;
          color: #333;
        }

        .action-btn:hover {
          background-color: #e0e0e0;
        }

        .pdf-btn {
          background-color: #f5f5f5;
          border: 1px solid #ddd;
          color: #333;
        }

        .pdf-btn:hover {
          background-color: #e0e0e0;
        }

        .docx-btn {
          background-color: #f5f5f5;
          border: 1px solid #ddd;
          color: #333;
        }

        .docx-btn:hover {
          background-color: #e0e0e0;
        }

        .speech-btn {
          background-color: #28a745;
          border: 1px solid #28a745;
          color: white;
        }

        .speech-btn:hover {
          background-color: #218838;
        }

        .speech-btn.stop {
          background-color: #dc3545;
          border: 1px solid #dc3545;
        }

        .speech-btn.stop:hover {
          background-color: #c82333;
        }

        .voice-controls {
          display: flex;
          gap: 8px;
          align-items: center;
        }

        .form-select {
          font-size: 0.8rem;
          padding: 5px;
          border-radius: 4px;
          border: 1px solid #ddd;
          background-color: #f5f5f5;
          color: #333;
        }

        .form-select:focus {
          outline: none;
          border-color: #007bff;
          box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
        }

        .nav-tabs {
          border-bottom: 1px solid #ddd;
          background-color: #f8f9fa;
        }

        .nav-tabs .nav-item {
          margin-bottom: -1px;
        }

        .nav-tabs .nav-link {
          border: 1px solid transparent;
          border-top-left-radius: 0.25rem;
          border-top-right-radius: 0.25rem;
          color: #495057;
          padding: 0.5rem 1rem;
          font-size: 0.9rem;
        }

        .nav-tabs .nav-link:hover {
          border-color: #e9ecef #e9ecef #dee2e6;
        }

        .nav-tabs .nav-link.active {
          color: #007bff;
          background-color: #fff;
          border-color: #dee2e6 #dee2e6 #fff;
        }

        @media (max-width: 768px) {
          .markdown-content h1 {
            font-size: 1.5rem;
          }

          .markdown-content h2 {
            font-size: 1.3rem;
          }

          .markdown-content p,
          .markdown-content li,
          .markdown-content th,
          .markdown-content td {
            font-size: 0.9rem;
          }

          .action-buttons {
            justify-content: center;
          }

          .nav-tabs .nav-link {
            font-size: 0.8rem;
            padding: 0.4rem 0.8rem;
          }
        }

        .rpv-default-layout__body {
          padding-top: 0.5rem;
        }

        .error-message {
          background-color: #343a40;
          color: white;
          padding: 15px;
          border-radius: 5px;
          text-align: center;
          margin: 20px 0;
        }

        .error-message p {
          margin: 0;
          font-size: 1rem;
        }

        .error-message .btn {
          margin-top: 10px;
          font-size: 0.9rem;
        }

        .zoom-controls {
          display: flex;
          gap: 10px;
          padding: 10px 15px;
          border-radius: 8px;
          width: fit-content;
          height: 50px;
          align-items: center;
        }

        .zoom-btn {
          color: #000;
          border: none;
          padding: 8px 10px;
          font-size: 16px;
          border-radius: 6px;
          cursor: pointer;
          transition: background-color 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .zoom-btn:hover {
          background-color: #1366d6;
        }

        .zoom-btn:disabled {
          background-color: #cbd5e0;
          cursor: not-allowed;
          color: #7a7a7a;
        }
      `}</style>

      <div className="row g-3" style={{ flex: "1 0 auto", marginTop: "4%", minHeight: "calc(100vh - 119.5px)" }}>
        <div className="col-md-6 col-sm-12" >
          <div className="d-flex justify-content-between align-items-center">
            <h2 className="">Document Preview</h2>
            <div className="zoom-controls">
              <button
                className="zoom-btn"
                onClick={handleZoomOut}
                disabled={zoomLevel === zoomLevels[0]}
                title="Zoom Out"
              >
                <FaSearchMinus />
              </button>
              <button
                className="zoom-btn"
                onClick={handleZoomIn}
                disabled={zoomLevel === zoomLevels[zoomLevels.length - 1]}
                title="Zoom In"
              >
                <FaSearchPlus />
              </button>
            </div>
          </div>
          {docData?.file_path ? (
            <div
              style={{
                height: '70vh',
                border: '1px solid #ddd',
                borderRadius: '5px',
                overflow: 'hidden',
              }}
            >
              <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
                <Viewer
                  fileUrl={docData.file_path}
                  plugins={[defaultLayoutPluginInstance, zoomPluginInstance]}
                  renderError={(error) => (
                    <div className="error-message">
                      <p>Failed to load PDF document.</p>
                      <button
                        className="btn btn-primary mt-2"
                        onClick={() => window.location.reload()}
                      >
                        Reload
                      </button>
                    </div>
                  )}
                />
              </Worker>
            </div>
          ) : (
            <div className="alert alert-warning">
              {docData ? 'No file path available' : 'No document data found in session storage'}
            </div>
          )}

          <div className="gemini-footer">
            <SiGooglegemini color="#4285F4" size={16} />
            <span>Powered by Gemini AI</span>
          </div>
        </div>

        <div className="col-md-6 col-sm-12 mb-2" >
          <div className="d-flex justify-content-between align-items-center ">
            <h2>Document Summary</h2>
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={handleBack}
              style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
            >
              <FaArrowLeft /> Back
            </button>
          </div>
          <div
            style={{
              height: '75vh',
              border: '1px solid #ddd',
              borderRadius: '5px',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <ul
              className="nav nav-tabs pb-0"
              style={{ paddingLeft: '20px', justifyContent: 'space-evenly', backgroundColor: '#f8f9fa', borderBottom: '1px solid #ddd' }}
            >
              {['short', 'medium', 'large'].map((tab) => (
                <li className="nav-item" key={tab}>
                  <button
                    className={`nav-link ${activeTab === tab ? 'active' : ''}`}
                    onClick={() => handleTabChange(tab)}
                  >
                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                  </button>
                </li>
              ))}
            </ul>

            <div
              style={{
                flex: 1,
                padding: '20px',
                overflowY: 'auto',
                position: 'relative',
              }}
            >
              {loading[activeTab] ? (
                <div className="d-flex justify-content-center align-items-center h-100">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : summary[activeTab] ? (
                <div className="summary-content">
                  <ReactMarkdown className="markdown-content">{summary[activeTab]}</ReactMarkdown>

                  <div className="action-buttons">
                    <button
                      className="action-btn"
                      onClick={() => copyToClipboard(summary[activeTab])}
                      title="Copy Summary"
                    >
                      <FaCopy /> Copy
                    </button>
                    <button
                      className="action-btn pdf-btn"
                      onClick={() => downloadAsPDF(summary[activeTab], activeTab)}
                      title="Download as PDF"
                    >
                      <FaDownload /> PDF
                    </button>
                    <button
                      className="action-btn docx-btn"
                      onClick={() => downloadAsDocx(summary[activeTab], activeTab)}
                      title="Download as DOCX"
                    >
                      <FaDownload /> DOCX
                    </button>
                    <div className="voice-controls">
                      <button
                        className={`action-btn speech-btn ${isSpeaking ? 'stop' : ''}`}
                        onClick={isSpeaking ? handleStop : handleSpeechToggle}
                        disabled={!summary[activeTab]}
                        title={isSpeaking ? 'Stop Speech' : 'Play Speech'}
                      >
                        {isSpeaking ? <FaRegStopCircle /> : <LuSpeech />}
                      </button>
                      <select
                        value={speechSpeed}
                        onChange={(e) => setSpeechSpeed(parseFloat(e.target.value))}
                        className="form-select"
                      >
                        <option value={0.8}>Slow</option>
                        <option value={1.0}>Normal</option>
                        <option value={1.2}>Fast</option>
                      </select>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="h-100 d-flex flex-column align-items-center justify-content-center">
                  <button
                    className="btn btn-primary mt-2"
                    onClick={() => fetchSummary(activeTab)}
                    disabled={loading[activeTab]}
                  >
                    {loading[activeTab] ? (
                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    ) : (
                      'Generate Summary'
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SummaryDetails;
